import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import {
    Avatar,
    Box,
    Tabs,
    Tab,
    Typography
} from '@mui/material';

import { GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-pro';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HistoryIcon from '@mui/icons-material/History';
import LockResetIcon from '@mui/icons-material/LockReset';
import PasswordIcon from '@mui/icons-material/Password';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';

import { useAbortController } from '../contexts/AbortControllerContext';
import ProjectContext from '../contexts/ProjectContext';

import ShortDataGrid from '../components/datagrid/ShortDataGrid';

import AddUserDialog from '../components/dialog/AddUserDialog';
import CreateRoleDialog from '../components/dialog/CreateRoleDialog';

import { itemIcons } from '../components/SideItems/SideItems';

import ControlBar from '../components/structure/ControlBar';
import ControlButton from '../components/structure/ControlButton';
import ControlDivider from '../components/structure/ControlDivider';
import ControlFeedback from '../components/structure/ControlFeedback';
import {useFetch} from '../components/auth/useFetch';



function stringToColor(string) {
    let hash = 0;
    let i;
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
}

export default function AccessManageView(props) {

    const viewname = 'Access Management'
    const { setIsLoading } = props
    const { isAuthenticated } = useAuth0();
    const { registerController, unregisterController } = useAbortController()
    const projectContext = useContext(ProjectContext);

    const apiRef = useGridApiRef()
    const unsavedChangesRef = useRef({
        rowsBeforeChange: {},
        rowsAfterChange: {},
    })

    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([])

    const [openUserDialog, setOpenUserDialog] = useState(false)
    // const [openCreateRoleDialog, setOpenCreateRoleDialog] = useState(false)

    const [isUnsavedChange, setIsUnsavedChange] = useState(false)
    const [status, setStatus] = useState('')

    const fetchWithAuth = useFetch()

    useEffect(() => {
        isAuthenticated && (() => {
            getAllRoles()
            .then(() => getAllUsers())
            .finally(() => setIsLoading(false))
        })()
    }, [isAuthenticated])

    const userDatagridColumns = [
        {field: 'icon', headerName: '', renderCell: (params) => 
            <Avatar 
                sx={{   bgcolor: stringToColor(params.row.name ?? ''),
                    border: '2px solid #274333',
                    fontSize: '15px',
                    width: 35,
                    height: 35
                }}
            >
                {`${(params.row.name[0] ?? 'X').toUpperCase()}${(params.row.name[1] ?? 'X').toUpperCase()}`}
            </Avatar>
        },
        { field: 'email', headerName: 'Email', width: 200},
        { field: 'name', headerName: 'Name', width: 125},
        { field: 'role_id', headerName:'Role', width: 100, type: 'singleSelect', valueOptions: roles, getOptionValue: (role) => role.id, getOptionLabel: (role) => role.name, editable: true },
        { field: 'last_login', headerName: 'Last Login', width: 160, renderCell: (params) => parseTimestamp(params.value) },
        { field: 'actions', headerName: 'Actions', width: 80, type:'actions',
            getActions: ({ id, row }) => [
                <GridActionsCellItem
                    icon={
                        <DoDisturbIcon style={{ fill: 'rgb(255, 0, 0)' }} />
                    }
                    label={`Revoke Access`}
                    onClick={() => rowRevokeAccess(id, row)}
                    showInMenu
                />,
                <GridActionsCellItem
                    icon={
                        <PasswordIcon style={{ fill: 'rgb(100, 130, 200)' }} />
                    }
                    label={"Reset Password"}
                    onClick={() => resetPass(row)}
                    showInMenu
                />,
                <GridActionsCellItem
                    icon={
                        <UndoIcon style={{ fill: 'rgb(100, 130, 200)' }} />
                    }
                    label={"Revert Change"}
                    onClick={() => {
                        rowRevertChange(id, row)
                    }}
                    disabled={unsavedChangesRef.current.rowsAfterChange[id] === undefined}
                    showInMenu
                />
            ]
        },
    ];

    const onUnsavedChange = () => {
        setIsUnsavedChange(true)
        setStatus('UNSAVED CHANGES')
    }

    const onSaveChange = () => {
        setIsUnsavedChange(false)
        setStatus('SAVED CHANGES')
        clearStatusAfterTimeout()
    }

    const onRevertChange = () => {
        setIsUnsavedChange(false)
        setStatus('')
    }

    const clearStatusAfterTimeout = () => {
        setTimeout(() => setStatus(''), 1000)
    }

    const updateRow = (rowID, updatedUser) => {
        setUsers(prev => {
            return prev.map(user => (user.user_id === rowID ? updatedUser : user))
        })
    }

    const rowRevokeAccess = (id, row) => {
        apiRef.current.updateRows([row])
        unsavedChangesRef.current.rowsAfterChange[id] = { ...row, _action: 'delete' }
        if (!unsavedChangesRef.current.rowsBeforeChange[id]){
            unsavedChangesRef.current.rowsBeforeChange[id] = row
        }
        onUnsavedChange()
    }

    const rowRevertChange = (id, row) => {
        apiRef.current.updateRows([unsavedChangesRef.current.rowsBeforeChange[id]])
        updateRow(id, unsavedChangesRef.current.rowsBeforeChange[id])
        delete unsavedChangesRef.current.rowsBeforeChange[id]
        delete unsavedChangesRef.current.rowsAfterChange[id]
        if(!Object.keys(unsavedChangesRef.current.rowsAfterChange).length){
            onRevertChange()
        }
    }

    const processRowUpdate = (newRow, oldRow) => {
        const updatedValues = Object.keys(oldRow).reduce((acc, key) => {
            if(oldRow[key] !== newRow[key]) {
                acc[key] = newRow[key]
            }
            return acc
        }, {})
        const id = newRow.id
        unsavedChangesRef.current.rowsAfterChange[id] = updatedValues
        if (!unsavedChangesRef.current.rowsBeforeChange[id]){
            unsavedChangesRef.current.rowsBeforeChange[id] = oldRow
        }
        updateRow(id, newRow)
        onUnsavedChange()

        return newRow
    }

    const getAllUsers = async () => {
        setIsLoading(true)
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/userdetailsadmin/`, {
            headers: { 
                "Content-type": "application/json; charset=UTF-8",
                'X-Project-ID': projectContext?.project_db
            }
        })
        .then(res => {
            if(!res.ok){
                throw new Error(res.statusText)
            }
            return res.json()
        })
        .then(res => setUsers(res))
        .catch(e => {
            setUsers([])
        })
        .finally(() => setIsLoading(false))
    }

    const getAllRoles = async () => {
        setIsLoading(true)
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/roles/`, {
            headers: { 
                "Content-type": "application/json; charset=UTF-8",
                'X-Project-ID': projectContext?.project_db
            }
        })
        .then(res => res.json())
        .then(res => setRoles(res.map(role => ({
                ...role,
                name: role.name.replace(new RegExp(`^Konsol.${projectContext?.project_db}.`), ''),
            })
        )))
        .catch(e => console.error(e))
        .finally(() => setIsLoading(false))
    }

    const resetPass = async (row) => {
        setIsLoading(true)
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/userdetailsadmin/password/`, {
            method: 'POST',
            body: JSON.stringify(row),
            headers: { 
                "Content-type": "application/json",
                'X-Project-ID': projectContext?.project_db
            }
        })
        .finally(() => setIsLoading(false))
    }

    const saveChanges = () => {
        Object.entries(unsavedChangesRef.current.rowsAfterChange).forEach(([id,row]) => {
            if (row?._action === 'delete'){
                revokeUserAccess(row)
            }
            else{
                updateUser(id, row)
            }
        })
        onSaveChange()
    }

    const revertChanges = () => {
        onRevertChange()
        const oldRows = Object.values(unsavedChangesRef.current.rowsBeforeChange)
        apiRef.current.updateRows(oldRows)
        oldRows.map(oldRow => updateRow(oldRow.user_id, oldRow))
        unsavedChangesRef.current = { rowsAfterChange: {}, rowsBeforeChange: {} }
    }

    const revokeUserAccess = async (row) => {
        setIsLoading(true)
        const body = { user_id: row.user_id, role_id: row.role_id }
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/userdetailsadmin/`, {
            method: 'DELETE',
            body: JSON.stringify(body),
            headers: { 
                "Content-type": "application/json",
                'X-Project-ID': projectContext?.project_db
            }
        })
        .then(() => getAllUsers())
        .finally(() => setIsLoading(false))
    }

    // TODO
    // Long term fix
    // Hold role changes in the ref
    // Backend needs role_id to remove and to add
    const updateUser = async (user_id, data) => {
        const from_role = unsavedChangesRef.current.rowsBeforeChange[user_id].role_id
        const to_role = data.role_id ?? from_role //Fix for if the same role is selected which doesnt save as an updated role
        setIsLoading(true)
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/userdetailsadmin/?user_id=${user_id}&from=${from_role}&to=${to_role}`, {
            method: 'PATCH',
            // body: JSON.stringify(data),
            headers: { 
                "Content-type": "application/json",
                'X-Project-ID': projectContext?.project_db
            }
        })
        .finally(() => setIsLoading(false))
    }

    const parseTimestamp = (timestamp) => {
        const now = Date.now();
        let ts = now
        if(!isNaN(Date.parse(timestamp))){
            ts = new Date(timestamp)
        }
        else{
            return 'Never';
        }
        const differenceInMs = now - ts;
        const differenceInMinutes = Math.floor(differenceInMs / (1000 * 60));
        const differenceInHours = Math.floor(differenceInMs / (1000 * 60 * 60));

        if(differenceInMinutes < 1){
            return 'Just Now'
        }
        else if (differenceInMinutes < 60) {
            return `${differenceInMinutes} minute${differenceInMinutes > 1 ? 's' : ''} ago`;
        }
        else if (differenceInHours < 72) {
            return `${differenceInHours} hour${differenceInHours > 1 ? 's' : ''} ago`;
        }
        else {
            const day = ts.getDate().toString().padStart(2, '0');
            const month = (ts.getMonth() + 1).toString().padStart(2, '0');
            const year = ts.getFullYear().toString().slice(-2);
            return `${day}/${month}/${year}`;
        }
    }

    return (
        <React.Fragment>
            <Box 
                display="flex" 
                flexDirection="column" 
                height='100%'
                width='100%'
            >
                <Box
                    width='100%'
                >
                    <ControlBar>
                        <Tabs
                            value={0}
                            indicatorColor="secondary"
                            variant="scrollable"
                            scrollButtons="auto">
                            <Tab
                                label={
                                    <Box display="flex" alignItems="center">
                                        {itemIcons[viewname]}
                                        <Box ml={1}>{viewname}</Box>
                                    </Box>
                                }
                                style={{ textTransform: 'none', fontWeight: 'bold', color: 'white' }} />
                        </Tabs>
                        <ControlDivider />
                        <Box
                            display='flex'
                            justifyContent='space-between'
                            width='150px'
                            border='1px solid rgb(125, 125, 125)'
                            borderRadius='10px'
                        >
                            <ControlButton
                                clicked={saveChanges}
                                title='Save'
                                disabled={Object.keys(unsavedChangesRef.current.rowsAfterChange).length ? false : true}
                            >
                                <SaveIcon style={{ fill: 'rgb(255, 255, 255)'}} />
                            </ControlButton>
                            <ControlFeedback>
                                <Typography
                                    fontSize='10px'
                                    fontWeight={600}
                                    width='100%'
                                    color={isUnsavedChange ? 'rgb(255, 165, 0)' : 'rgb(176, 236, 175)'}
                                >
                                    {status}
                                </Typography>
                            </ControlFeedback>
                            <ControlButton
                                clicked={revertChanges}
                                title='Revert Changes'
                                disabled={Object.keys(unsavedChangesRef.current.rowsAfterChange).length ? false : true}
                            >
                                <HistoryIcon style={{ fill: 'rgb(255, 255, 255)'}} />
                            </ControlButton>
                        </Box>
                        <ControlDivider />
                        <ControlButton
                            clicked={() => setOpenUserDialog(true)}
                            title='Add New User'
                        >
                            <GroupAddIcon style={{ fill: 'rgb(255, 255, 255)'}} />
                        </ControlButton>
                        {/* <ControlButton
                            clicked={() => setOpenCreateRoleDialog(true)}
                            title='View Roles'
                        >
                            <PermContactCalendarIcon style={{ fill: 'rgb(255, 255, 255)'}} />
                        </ControlButton> */}

                    </ControlBar>
                </Box>
                <Box 
                    display="flex" 
                    flexDirection="row"
                    sx={{
                        flex: 1,
                        overflow:'hidden',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                    }}
                >
                    <Box 
                        width='100%'
                        height="100%"
                    >
                        <ShortDataGrid
                            apiRef={apiRef}
                            columns={userDatagridColumns} 
                            rows={users.map(user => ({id: user.user_id, ...user}) )}
                            hideFooter={true}
                            getRowClassName={({ id }) => {
                                const rowsAfterChange = unsavedChangesRef.current.rowsAfterChange[id]
                                if (rowsAfterChange){
                                    if(rowsAfterChange._action === 'delete'){
                                        return 'row--delete'
                                    }
                                    return 'row--edit'
                                }
                                return ''
                            }}
                            processRowUpdate={processRowUpdate}
                        />
                    </Box>
                </Box>
                {openUserDialog && 
                    <AddUserDialog
                        availableRoles={roles.map(role => ({ name: role.name, value: role.id }) )}
                        onSuccess={getAllUsers}
                        open={openUserDialog}
                        setOpen={setOpenUserDialog}
                        // setOpenRoles={setOpenCreateRoleDialog}
                    />
                }
                {/* <CreateRoleDialog
                    availableRoles={roles.map(role => ({ name: role.name, value: role.id }) )}
                    open={openCreateRoleDialog}
                    setOpen={setOpenCreateRoleDialog}
                /> */}
            </Box>
        </React.Fragment>
    )
}